import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileDown, PauseCircle, PlayCircle } from "lucide-react";

import { CircularMeter, Counter, LinearMeter } from "../components/atmos/Meters";
import {
  completionPercent,
  formatDuration,
  formatTimestamp,
  getDashboardOverview,
  getProfile,
} from "../lib/profileEngine";
import { getAccessibleDashboardProfiles, getLockedProfileCount } from "../lib/profileVisibility";

const Dashboard = () => {
  const profile = getProfile();
  const [dashboard, setDashboard] = useState({ profiles: [], recentAttempts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const nextDashboard = await getDashboardOverview();
        if (!ignore) {
          setDashboard(nextDashboard);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || "Unable to load profile dashboard.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, []);

  const visibleProfiles = useMemo(() => getAccessibleDashboardProfiles(dashboard.profiles), [dashboard.profiles]);
  const inviteOnlyCount = useMemo(
    () => Math.max(0, getLockedProfileCount(dashboard.profiles.map((entry) => entry.profile))),
    [dashboard.profiles],
  );

  const summary = useMemo(() => {
    const profileCount = visibleProfiles.length;
    const attemptCount = dashboard.profiles.reduce((sum, entry) => sum + (entry.summary?.attemptCount || 0), 0);
    const reportCount = dashboard.profiles.reduce((sum, entry) => sum + (entry.summary?.completedCount || 0), 0);
    const liveCount = visibleProfiles.filter((entry) => entry.activeAttempt).length;
    return { profileCount, attemptCount, reportCount, liveCount };
  }, [dashboard.profiles, visibleProfiles]);

  return (
    <main className="relative pt-8 md:pt-10 lg:pt-24 pb-12 md:pb-14 px-5 md:px-10 lg:px-16 lg:pl-32 xl:pl-36" data-testid="dashboard">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85 }}
        className="grid grid-cols-12 gap-6 md:gap-8 mb-8 md:mb-10"
      >
        <div className="col-span-12 lg:col-span-8">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">
            ◐ PARTICIPANT LEDGER · {profile.fullName || "UNNAMED PARTICIPANT"}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl xl:text-6xl tracking-tighter leading-[1.02] text-ink">
            Your profile dashboard
            <br />
            <span className="italic text-gold gold-glow">for progress, reports, and next steps.</span>
          </h1>
          <p className="text-ink-2 mt-5 max-w-2xl">
            Continue a saved profile, open completed reports, or begin another available profile from one place.
          </p>
        </div>

        <div className="col-span-12 lg:col-span-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 gap-3">
          {[
            { label: "PROFILES", value: summary.profileCount },
            { label: "ATTEMPTS", value: summary.attemptCount },
            { label: "REPORTS", value: summary.reportCount },
            { label: "LIVE", value: summary.liveCount },
          ].map((item) => (
            <div key={item.label} className="glass rounded-sm p-4">
              <div className="font-mono text-[16px] tracking-widest-2 text-ink">{item.label}</div>
              <div className="font-mono text-3xl text-gold mt-2 gold-glow tabular">
                <Counter value={item.value} />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {error ? (
        <div className="glass rounded-sm p-5 mb-8 text-[color:var(--warn-text)]">{error}</div>
      ) : null}

      <section className="mb-8">
        <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ LIVE PROFILE LIBRARY</div>
        <h2 className="font-serif text-3xl text-ink mb-6">Available profiles</h2>
        <div className="grid grid-cols-12 gap-4 md:gap-5">
          {loading ? (
            <div className="col-span-12 glass rounded-sm p-5 text-ink-2">Loading profiles...</div>
          ) : visibleProfiles.length === 0 ? (
            <div className="col-span-12 glass rounded-sm p-5 text-ink-2">No live profiles are available yet.</div>
          ) : (
            visibleProfiles.map((entry) => {
              const activeAttempt = entry.activeAttempt;
              const latestCompleted = entry.latestCompletedAttempt;
              const progress = activeAttempt ? Math.round(completionPercent(activeAttempt, entry.profile.questionCount)) : 0;
              return (
                <article key={entry.profile.id} className="col-span-12 xl:col-span-6 glass-strong rounded-sm overflow-hidden h-full">
                  <div className="grid grid-cols-12 h-full">
                    <div className="col-span-12 md:col-span-5 relative min-h-[220px]">
                      <div className="absolute inset-0 dashboard-card-mood" />
                      <div className="absolute top-6 left-6 font-mono text-[16px] tracking-widest-2 text-gold">
                        ◇ {activeAttempt ? (activeAttempt.status === "paused" ? "PAUSED" : "IN FLIGHT") : entry.retakeLocked ? "COMPLETED" : "READY"}
                      </div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <div className="font-mono text-[16px] tracking-widest-2 text-ink">{entry.profile.duration.toUpperCase()}</div>
                        <h3 className="font-serif text-3xl text-ink mt-1">{entry.profile.name}</h3>
                      </div>
                    </div>

                    <div className="col-span-12 md:col-span-7 p-6 md:p-8 flex flex-col justify-between h-full">
                      <div className="flex flex-col lg:flex-row items-start justify-between gap-6">
                        <div>
                          <p className="text-ink-2 max-w-xl">{entry.profile.tagline}</p>
                          <div className="flex items-center gap-6 mt-5 font-mono text-[16px] tracking-widest-2 text-ink">
                            <span>{entry.profile.questionCount} ITEMS</span>
                            {entry.profile.id !== "ssp" ? <span>{entry.profile.factorCount} FACTORS</span> : null}
                          </div>
                          {entry.cooldown?.blocked ? (
                            <div className="mt-3 text-base text-[color:var(--warn-text)]">
                              Cooldown active until {new Date(entry.cooldown.availableAt).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </div>
                          ) : null}
                        </div>
                        <CircularMeter value={progress} size={130} stroke={4} label="PROGRESS" />
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-6">
                        {[
                          { label: "ATTEMPTS", value: entry.summary?.attemptCount || 0 },
                          { label: "REPORTS", value: entry.summary?.completedCount || 0 },
                          { label: "CURRENT ITEM", value: activeAttempt?.currentQuestionId || 0 },
                          { label: "ACTIVE TIME", value: formatDuration(activeAttempt?.analytics?.totalActiveMs || 0) },
                        ].map((item) => (
                          <div key={item.label} className="rounded-sm border border-line px-3 py-3 min-w-0">
                            <div className="font-mono text-[12px] text-ink truncate">{item.label}</div>
                            <div className="font-mono text-lg text-gold mt-2">{item.value}</div>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-3 mt-6">
                        {activeAttempt || entry.canStart ? (
                          <Link to={`/profile/${entry.profile.id}`} className="btn-gold w-full sm:w-auto">
                            {activeAttempt ? "Resume Profile" : "Start Profile"} <ArrowUpRight className="w-4 h-4" />
                          </Link>
                        ) : null}
                        {latestCompleted ? (
                          <Link
                            to={`/report/${entry.profile.id}?attempt=${latestCompleted.id}`}
                            className={`${activeAttempt || entry.canStart ? "btn-ghost" : "btn-gold"} w-full sm:w-auto justify-center`}
                          >
                            Open Latest Report
                          </Link>
                        ) : null}
                      </div>
                      {entry.retakeLocked ? (
                        <p className="mt-3 text-base text-ink-2" data-testid="retake-locked">
                          Completed. To take this profile again, purchase a retake through your Redacamy course.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
        {inviteOnlyCount > 0 ? (
          <div className="glass rounded-sm p-5 mt-4 text-base text-ink-2 leading-relaxed">
            Additional charge-based profiles are opened through direct participant links shared by email.
            They will appear here once a participant has started or completed them.
          </div>
        ) : null}
      </section>

      <div className="grid grid-cols-12 gap-6">
        <section className="col-span-12 lg:col-span-8">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ RECENT ATTEMPT HISTORY</div>
          <h2 className="font-serif text-3xl text-ink mb-6">Current, paused, and completed profile runs</h2>
          <div className="space-y-3">
            {loading ? (
              <div className="glass rounded-sm p-5 text-ink-2">Loading attempt history...</div>
            ) : dashboard.recentAttempts.length === 0 ? (
              <div className="glass rounded-sm p-5 text-ink-2">No profile runs recorded yet. Begin a profile to create your first attempt.</div>
            ) : (
              dashboard.recentAttempts.map((attempt) => (
                <div key={attempt.id} className="glass rounded-sm p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        {attempt.status === "completed" ? (
                          <FileDown className="w-4 h-4 text-gold" />
                        ) : attempt.status === "paused" ? (
                          <PauseCircle className="w-4 h-4 text-gold" />
                        ) : (
                          <PlayCircle className="w-4 h-4 text-gold" />
                        )}
                        <div className="font-serif text-xl text-ink">{attempt.profileName}</div>
                      </div>
                      <div className="font-mono text-[16px] tracking-widest-2 text-ink mt-2">
                        STARTED · {formatTimestamp(attempt.startedAt)}
                      </div>
                      <div className="text-ink-2 text-base mt-2">
                        {attempt.status === "completed"
                          ? `Completed · ${attempt.report?.secondaryDimension?.label || "Secondary"} average ${attempt.report?.secondaryDimension?.average ?? 0}`
                          : `Item ${attempt.currentQuestionId} · ${completionPercent(attempt)}% complete`}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                      {attempt.status === "completed" ? (
                        <Link to={`/report/${attempt.profileId}?attempt=${attempt.id}`} className="btn-ghost text-[16px] py-2 px-3 w-full sm:w-auto justify-center">
                          Open Report
                        </Link>
                      ) : (
                        <Link to={`/profile/${attempt.profileId}`} className="btn-ghost text-[16px] py-2 px-3 w-full sm:w-auto justify-center">
                          Open Profile
                        </Link>
                      )}
                    </div>
                  </div>

                  {attempt.status !== "completed" ? (
                    <div className="mt-4">
                      <LinearMeter value={completionPercent(attempt)} sublabel={`Tracked time · ${formatDuration(attempt.analytics.totalActiveMs)}`} />
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </section>

        <section className="col-span-12 lg:col-span-4">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ PARTICIPANT GUIDE</div>
          <h2 className="font-serif text-3xl text-ink mb-6">What you can do here</h2>
          <div className="space-y-3">
            {[
              "Open any available profile and continue from the point where you last stopped.",
              "Paused progress and skipped items remain available when you return to a profile.",
              "Completed profiles include private reports and downloadable PDF summaries.",
              "Multiple spiritual and life-stage profiles can be managed from the same dashboard.",
            ].map((item) => (
              <div key={item} className="glass rounded-sm p-5 text-ink-2">{item}</div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Dashboard;
